// DataSourcesPage.js - Public read-only data sources table
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./DataSourcesPage.css";

// API base URL - use environment variable in production
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

/**
 * Calculate collection status based on refresh frequency and last modified date.
 * If the time since last modification exceeds the refresh interval, status is "Delayed".
 * @param {string} refreshFrequency - One of: "Weekly", "Monthly", "Quarterly", "Annual"
 * @param {string} lastModifiedDate - ISO date string of last modification
 * @returns {string} - "On-time" or "Delayed"
 */
function calculateCollectionStatus(refreshFrequency, lastModifiedDate) {
    if (!lastModifiedDate || !refreshFrequency) {
        return "On-time"; // Default if data is missing
    }

    const lastModified = new Date(lastModifiedDate);
    const now = new Date();

    // Calculate the expected next refresh date based on frequency
    let expectedNextRefresh = new Date(lastModified);

    switch (refreshFrequency) {
        case "Weekly":
            expectedNextRefresh.setDate(expectedNextRefresh.getDate() + 7);
            break;
        case "Monthly":
            expectedNextRefresh.setMonth(expectedNextRefresh.getMonth() + 1);
            break;
        case "Quarterly":
            expectedNextRefresh.setMonth(expectedNextRefresh.getMonth() + 3);
            break;
        case "Annual":
            expectedNextRefresh.setFullYear(expectedNextRefresh.getFullYear() + 1);
            break;
        default:
            return "On-time"; // Unknown frequency, default to on-time
    }

    // If current date is past the expected next refresh date, status is Delayed
    return now > expectedNextRefresh ? "Delayed" : "On-time";
}

function DataSourcesPage() {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchRecords();
    }, []);

    const fetchRecords = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE}/api/datasources`);
            if (!response.ok) {
                throw new Error("Failed to load data sources");
            }
            const data = await response.json();
            setRecords(data.records || []);
        } catch (err) {
            setError(err.message || "Couldn't load data sources");
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
            });
        } catch {
            return dateStr;
        }
    };

    const getStatusBadgeClass = (status, type) => {
        if (type === "collection") {
            return status === "On-time" ? "status-badge on-time" : "status-badge delayed";
        }
        return status === "Active" ? "status-badge active" : "status-badge inactive";
    };

    return (
        <div className="container-fluid p-4">
            {/* Navigation */}
            <div className="datasources-nav">
                <Link to="/" className="back-link">
                    ← Back to Optibot
                </Link>
            </div>

            {/* Header Banner */}
            <div className="datasources-header">
                <h1>
                    <i className="fas fa-database"></i> Data Collection Report
                </h1>
                <p className="helper-text">
                    <i className="fas fa-clock"></i> Planned frequency for refreshing each data record
                </p>
                <p className="helper-text">
                    <i className="fas fa-calendar-alt"></i> Most recent date for refreshing each data record
                </p>
            </div>

            {/* Data Table Card */}
            <div className="datasources-card">
                {loading ? (
                    <div className="datasources-loading">
                        <div className="spinner"></div>
                        <p>Loading data sources...</p>
                    </div>
                ) : error ? (
                    <div className="datasources-error">
                        <div className="error-icon">⚠️</div>
                        <p>{error}</p>
                        <button className="btn btn-primary" onClick={fetchRecords}>
                            Try Again
                        </button>
                    </div>
                ) : records.length === 0 ? (
                    <div className="datasources-empty">
                        <p>No data sources found.</p>
                    </div>
                ) : (
                    <table className="datasources-table">
                        <thead>
                            <tr>
                                <th>Data Record</th>
                                <th>Sources</th>
                                <th>Refresh Frequency</th>
                                <th>Last Modified Date</th>
                                <th>Data Collection Status</th>
                                <th>Record Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.map((record) => (
                                <tr key={record.id}>
                                    <td>{record.data_record}</td>
                                    <td>{record.sources}</td>
                                    <td>{record.refresh_frequency}</td>
                                    <td>{formatDate(record.last_modified_date)}</td>
                                    <td>
                                        <span className={getStatusBadgeClass(calculateCollectionStatus(record.refresh_frequency, record.last_modified_date), "collection")}>
                                            {calculateCollectionStatus(record.refresh_frequency, record.last_modified_date)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={getStatusBadgeClass(record.record_status, "record")}>
                                            {record.record_status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default DataSourcesPage;
